import { Woonstad_Foundation_Portfolio_Api_Models_WRM_StadswonenAdvertisement } from '@woonstad/api-client-stadswonen-aanbod';
import { Woonstad_Foundation_Portfolio_Api_Features_Advertising_Models_Advertisement, Woonstad_Foundation_Portfolio_Api_Features_Nieuwbouw_Models_Api_ExpandedNieuwbouwType, Woonstad_Foundation_Portfolio_Api_Features_Woonstad_Models_AdvertisementDetailV3, Woonstad_Foundation_Portfolio_Api_Features_Woonstad_Models_NieuwbouwTypeDetailV3 } from '@woonstad/api-client-woonstad-aanbod';
import { hasValue } from '@woonstad/helpers-utils';

import { TagProps } from '../tag/tag';
import { mapAdvertStatusToAction } from './map-advert-status-to-action';

type AdvertStatusAction = ReturnType<typeof mapAdvertStatusToAction>;

const TAG_TYPE_BY_ACTION: Record<NonNullable<AdvertStatusAction>, TagProps['type']> = {
  OPEN_FOR_REACTIONS: 'success',
  JUST_CLOSED_FOR_REACTIONS: 'warning',
  ALMOST_CLOSED_FOR_REACTIONS: 'warning',
  NOT_OPEN_FOR_REACTIONS: 'error',
};

function getTagTypeFromAdvertStatus(action: AdvertStatusAction): TagProps['type'] {
  return action ? TAG_TYPE_BY_ACTION[action] ?? 'neutral' : 'neutral';
}

export function getObjectType(advert: Woonstad_Foundation_Portfolio_Api_Features_Advertising_Models_Advertisement | Woonstad_Foundation_Portfolio_Api_Features_Woonstad_Models_AdvertisementDetailV3 | Woonstad_Foundation_Portfolio_Api_Features_Nieuwbouw_Models_Api_ExpandedNieuwbouwType | Woonstad_Foundation_Portfolio_Api_Features_Woonstad_Models_NieuwbouwTypeDetailV3 | undefined) {
  if (hasValue(advert) && advert.variant === 'normaal') {
    if (hasValue((advert as Woonstad_Foundation_Portfolio_Api_Features_Advertising_Models_Advertisement).salesInformation?.objectType)) {
      return (advert as Woonstad_Foundation_Portfolio_Api_Features_Advertising_Models_Advertisement).salesInformation?.objectType;
    }

    if (hasValue((advert as Woonstad_Foundation_Portfolio_Api_Features_Woonstad_Models_AdvertisementDetailV3).salesInformation?.salesObjectType)) {
      return (advert as Woonstad_Foundation_Portfolio_Api_Features_Woonstad_Models_AdvertisementDetailV3).salesInformation?.salesObjectType;
    }
  }

  if (hasValue(advert) && advert.variant === 'nieuwbouw') {
    return (advert as Woonstad_Foundation_Portfolio_Api_Features_Nieuwbouw_Models_Api_ExpandedNieuwbouwType).unitType;
  }

  return undefined;
}

type Advert = Woonstad_Foundation_Portfolio_Api_Features_Advertising_Models_Advertisement | Woonstad_Foundation_Portfolio_Api_Features_Woonstad_Models_AdvertisementDetailV3 | Woonstad_Foundation_Portfolio_Api_Features_Nieuwbouw_Models_Api_ExpandedNieuwbouwType | Woonstad_Foundation_Portfolio_Api_Features_Woonstad_Models_NieuwbouwTypeDetailV3;

function getAdvertStatus(advert: Advert): string | undefined {
  if ('status' in advert && typeof advert.status === 'string') return advert.status;
  if ('state' in advert && advert.state?.name) return advert.state.name;
  return undefined;
}

function getActiveCampaignName(advert: Advert): string | undefined {
  const campaign = advert?.campaign;
  if (!campaign?.name || !campaign?.endDate) return undefined;
  if (new Date(campaign.endDate) > new Date()) return campaign.name;
  return undefined;
}

export const advertCardTagsWoonstad = (
  advert?: Advert,
): {
  type: TagProps['type'];
  text: string;
}[] => {
  if (!advert) return [];

  const objectType = getObjectType(advert);
  const objectTypeTag = objectType ? { type: 'neutral' as const, text: objectType } : null;

  const advertStatus = getAdvertStatus(advert);
  const advertStatusAction = advertStatus ? mapAdvertStatusToAction(advertStatus) : null;
  const advertStatusTag = advertStatus && advertStatus !== 'beschikbaar'
    ? { type: getTagTypeFromAdvertStatus(advertStatusAction), text: advertStatus }
    : null;

  const advertCampaign = getActiveCampaignName(advert);
  const campaignTag = advertCampaign ? { type: 'warning' as const, text: advertCampaign } : null;

  return [objectTypeTag, advertStatusTag, campaignTag].filter(
    (tag): tag is { type: TagProps['type']; text: string } => tag !== null,
  );
};

export const advertCardTagsStadswonen = (
  advert: Woonstad_Foundation_Portfolio_Api_Models_WRM_StadswonenAdvertisement,
): {
  type: TagProps['type'];
  text: string;
}[] => {
  const untilDate = advert.isoUntil ? new Date(advert.isoUntil) : undefined;

  const untilParsedTag = untilDate
    ? Intl.DateTimeFormat('nl-NL', {
      month: '2-digit',
      day: '2-digit',
    }).format(untilDate)
    : undefined;

  const tags = [];

  if (advert.objectType) {
    tags.push({
      type: 'neutral' as const,
      text: advert.objectType,
    });
  }

  if (untilParsedTag) {
    tags.push({
      type: 'success' as const,
      text: `Reageer t/m ${untilParsedTag}`,
    });
  }

  if (advert.campaign) {
    tags.push({
      type: 'warning' as const,
      text: advert.campaign,
    });
  }

  return tags;
};
